// Stored records, brought forward a version at a time.
//
// `DB_VERSION` in core/storage.js is 1 and cannot change — the object store is
// generic and the chassis owns it — so the version that matters is the one on
// the ROW. A row written before the strategy engine has no `v`; it is a v1 row,
// and everything the engine now reads from it (which strategy printed it, what
// the model thought, what the price closed at) is simply absent.
//
// Absent is not the same as null. A missing field reads as `undefined`, which
// prints as nothing, sorts unpredictably and silently skips every
// `Number.isFinite` guard — so the migration writes the nulls in explicitly and
// stamps `v: 2`. What it never does is invent a value: a v1 ticket was printed
// by Classic, so `strategyId` is known; nothing knew its closing price, so
// `closingPrice` is null and stays null.
//
// Idempotent by construction: a row that already says `v: 2` is handed back
// untouched, and a v1 row that already carries a field keeps the value it has.

export const RECORD_VERSION = 2;

/** Which strategy a row written before strategies existed came from. */
export const LEGACY_STRATEGY_ID = 'classic-four-ways';
export const LEGACY_STRATEGY_NAME = 'Classic Four Ways';

/**
 * What v2 adds to each entity. A key already present on the row wins — these
 * are the values for rows that never had the field at all.
 */
export const V2_DEFAULTS = Object.freeze({
  multi: Object.freeze({
    strategyId: LEGACY_STRATEGY_ID,
    strategyVersion: 1,
    label: LEGACY_STRATEGY_NAME,
    modelProbability: null,
    fairProbability: null,
    edge: null,
    ev: null,
    stakeRule: 'flat',
  }),
  leg: Object.freeze({
    matchKey: null,
    sport: null,
    bookmakerKey: null,
    modelProbability: null,
    fairProbability: null,
    edge: null,
    closingPrice: null,
    closingFair: null,
    clv: null,
    result: null,
    resultSource: null,
    settledAt: null,
  }),
  'bet-record': Object.freeze({
    strategyId: LEGACY_STRATEGY_ID,
    clv: null,
    pnl: null,
    autoSettle: true,
    resultSource: null,
  }),
});

/** The version a row is at. A row with no `v` is v1 — the shape before this file. */
export const rowVersion = (row) => (Number.isInteger(row?.v) ? row.v : 1);

/**
 * Bring one stored row up to `RECORD_VERSION`.
 *
 * Returns the row it should be stored as and whether anything changed, so the
 * caller can write back only what it has to — a full rewrite of a year of
 * history on every boot is a lot of IndexedDB for no reason.
 *
 * A row of an entity this file knows nothing about (a strategy someone saved,
 * whatever a later slice adds) is handed straight back: not stamped, not
 * defaulted, not touched.
 */
export function migrateRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return { row, changed: false };
  if (rowVersion(row) >= RECORD_VERSION) return { row, changed: false };

  const defaults = V2_DEFAULTS[row.entity];
  if (!defaults) return { row, changed: false };

  const next = { ...row, v: RECORD_VERSION };
  for (const [key, value] of Object.entries(defaults)) {
    if (next[key] === undefined) next[key] = value;
  }
  return { row: next, changed: true };
}

/**
 * Every row, migrated. `rows` is what comes back in the order it comes back;
 * `changed` is the subset that has to be written.
 */
export function migrateRows(rows) {
  const changed = [];
  const out = (rows ?? []).map((r) => {
    const result = migrateRow(r);
    if (result.changed) changed.push(result.row);
    return result.row;
  });
  return { rows: out, changed };
}
